import type { Theme, ThemeOptions } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const INPUT_FG = '#1E2A3A';

/** Layout + indicator styling for `Autocomplete` (field chrome from `Input` / `SmInputBase-root`). */
export function autocompleteThemeComponents(theme: Theme): ThemeOptions['components'] {
  const iconColor = theme.palette.action.active;
  const plainIconColor = INPUT_FG;
  const selectedOptionBg = theme.palette.primary.light;

  return {
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: <KeyboardArrowDownIcon />
      },
      styleOverrides: {
        paper: {
          '&.SmSelectMenu-paper': {
            backgroundColor: theme.palette.common.white,
            borderRadius: '10px',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 'none'
          }
        },
        listbox: {
          '&.SmSelectMenu-list': {
            backgroundColor: theme.palette.common.white,
            '& .MuiAutocomplete-option[aria-selected="true"]': {
              backgroundColor: `${selectedOptionBg} !important`
            },
            '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
              backgroundColor: `${selectedOptionBg} !important`
            },
            '& .MuiAutocomplete-option[aria-selected="true"].Mui-focusVisible': {
              backgroundColor: `${selectedOptionBg} !important`
            }
          }
        },
        root: {
          '&.SmAutocomplete-root': {
            display: 'block',
            width: '100%',
            '& .SmInputBase-root': {
              position: 'relative',
              '& .MuiInputBase-input': {
                paddingInlineEnd: '45px'
              },
              '&:has(.SmInputAdornment-combinedEnd) .MuiInputBase-input': {
                paddingInlineEnd: '72px'
              },
              '& .MuiInputAdornment-positionEnd': {
                position: 'absolute',
                insetInlineEnd: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                margin: 0,
                maxHeight: 'none',
                height: 'auto',
                gap: 4,
                color: iconColor
              },
              '& .SmInputAdornment-combinedEnd': {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 4
              },
              '& .SmInputAdornment-customEndIcon': {
                display: 'inline-flex',
                alignItems: 'center',
                flexShrink: 0,
                '& .MuiSvgIcon-root': {
                  fontSize: 18,
                  width: 18,
                  height: 18
                }
              },
              '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
                margin: 0,
                padding: 0,
                color: iconColor,
                backgroundColor: 'transparent',
                borderRadius: 0,
                transition: 'none'
              }
            },
            '&.MuiAutocomplete-multiple .SmInputBase-root': {
              flexWrap: 'nowrap',
              overflow: 'hidden'
            },
            '&.MuiAutocomplete-multiple .MuiInputAdornment-positionStart': {
              flex: '1 1 0%',
              width: 0,
              minWidth: 0,
              overflow: 'hidden',
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'center',
              margin: 0
            },
            '&.MuiAutocomplete-multiple .MuiInputBase-input': {
              flex: '0 0 auto',
              width: 30,
              minWidth: 30
            },
            '&.MuiAutocomplete-multiple .MuiAutocomplete-tag': {
              flexShrink: 0,
              margin: 0
            },
            '&.SmAutocomplete-noClear .MuiAutocomplete-clearIndicator': {
              display: 'none'
            },
            // The 30px input above is for compact chip fields. A search has to show what is
            // being typed, and its chips wrap onto the next line. `searchable` is the invoice
            // form's class; `searchableSelect` is the shared component's.
            '&.SmAutocomplete-supplierSuggest.MuiAutocomplete-multiple .SmInputBase-root, &.SmAutocomplete-searchable.MuiAutocomplete-multiple .SmInputBase-root, &.SmAutocomplete-searchableSelect.MuiAutocomplete-multiple .SmInputBase-root':
              {
                flexWrap: 'wrap',
                overflow: 'visible',
                height: 'auto'
              },
            '&.SmAutocomplete-supplierSuggest.MuiAutocomplete-multiple .MuiInputAdornment-positionStart, &.SmAutocomplete-searchable.MuiAutocomplete-multiple .MuiInputAdornment-positionStart, &.SmAutocomplete-searchableSelect.MuiAutocomplete-multiple .MuiInputAdornment-positionStart':
              {
                flex: '1 1 auto',
                width: 'auto',
                minWidth: 0,
                overflow: 'visible',
                flexWrap: 'wrap'
              },
            '&.SmAutocomplete-supplierSuggest.MuiAutocomplete-multiple .MuiInputBase-input, &.SmAutocomplete-searchable.MuiAutocomplete-multiple .MuiInputBase-input, &.SmAutocomplete-searchableSelect.MuiAutocomplete-multiple .MuiInputBase-input':
              {
                flex: '1 1 140px',
                width: 'auto',
                minWidth: 140
              }
          },
          '&.SmAutocomplete-appearance-plain .SmInputBase-root': {
            '& .MuiInputAdornment-positionEnd': {
              color: plainIconColor
            },
            '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
              color: `${plainIconColor} !important`,
              '& .MuiSvgIcon-root': {
                color: `${plainIconColor} !important`
              }
            },
            '&.Mui-disabled .MuiAutocomplete-popupIndicator, &.Mui-disabled .MuiAutocomplete-clearIndicator, & .Mui-disabled.MuiAutocomplete-popupIndicator, & .Mui-disabled.MuiAutocomplete-clearIndicator':
              {
                color: 'rgba(30, 42, 58, 0.4) !important',
                '& .MuiSvgIcon-root': {
                  color: 'rgba(30, 42, 58, 0.4) !important'
                }
              }
          }
        }
      }
    }
  };
}
